import React, { useEffect } from "react";
import { makeLazyLoader } from "../../../common/ui/lazy-load";
import PageViewFramework from "../framework/pageview/PageViewFramework";
import ReduxProvider from "../../../common/providers/ReduxProvider";
import GlobalFontProvider from "../../../common/providers/fonts/GlobalFontProvider";
import StyleResetProvider from "../../../common/providers/StyleResetProvider";
import ChameleonBrandProvider from "../../../common/providers/brand/ChameleonBrandProvider";
import MosaicProvider from "../../../common/providers/MosaicProvider";
import mosaicMiddleware, {
  reset as resetMosaicMiddleware,
} from "../framework/api/core/middleware";
import reducer from "../framework/api/reducer";
import BrandedPageTitleProvider from "../../../common/providers/BrandedPageTitleProvider";
import { ToastContainer } from "@vitalsource/vstui";
import MosaicSessionGate from "../../../common/providers/mosaic/MosaicSessionGate";
import useMonitoringAttribute from "../../../common/ui/useMonitoringAttribute";
import StaticLoader from "../../../main/StaticLoader";
import ReduxLocaleProvider from "../framework/providers/ReduxLocaleProvider";
import { colors } from "chameleonjs";
import VSTUIProvider from "../../../common/providers/VSTUIProvider";
import FaviconProvider from "../../../common/providers/FaviconProvider";
import ImageZoomFromPage from "../ui/default/features/image-zoom/ImageZoomFromPage";
import useIsTinyScreen from "../ui/default-mobile/common/mobileBreakpoints";
import DebugDialog from "../ui/default/features/debug/DebugDialog";
import { isTouchDevice } from "../../../common/utils/platform";
import CustomerDebug from "../ui/default/features/debug/CustomerDebug";
import { GeneralDownloadApp } from "../ui/default/features/download-apps/general-download-app";
import SkipFooterButton from "./utils/SkipFooterButton";
import SkipMainButton from "./utils/SkipMainButton";
import CookieCat from "./cookies/CookieCat";

const loader = makeLazyLoader();
const DefaultReader = loader.lazy(() => import("../ui/default"));
const DefaultMobileReader = loader.lazy(() => import("../ui/default-mobile"));
const ImmersiveReader = loader.lazy(() => import("../ui/immersive"));
const KiddieReader = loader.lazy(() => import("../ui/kiddie"));
const MinimalReader = loader.lazy(() => import("../ui/minimal"));

const middlewares = [mosaicMiddleware];

enum Reader {
  IMMERSIVE = "Immersive",
  DEFAULT = "Default",
  KIDDIE = "Kiddie",
  MINIMAL = "Minimal",
}

/**
 * This component is responsible for initializing the general reader
 * dependencies/framework and lazy-loading the specific UI treatment
 * we should be using
 */
export default function ReaderMain({ location, match }) {
  const isSmallScreen = useIsTinyScreen();

  useEffect(() => {
    resetMosaicMiddleware();
  }, []);

  useMonitoringAttribute("vbid", match.params.vbid);

  function getReaderUI(brand) {
    switch (brand.Reader) {
      case Reader.MINIMAL:
        return <MinimalReader />;
      case Reader.KIDDIE:
        return <KiddieReader />;
      case Reader.IMMERSIVE:
        return <ImmersiveReader />;
      default:
        if (isSmallScreen && isTouchDevice()) {
          // use mobile reader only on mobile, desktop uses the "compact view"
          // todo consider use of any-pointer: fine (desktop touchscreen)
          // but don't rock the boat, leave for now
          return <DefaultMobileReader />;
        }
        return <DefaultReader />;
    }
  }

  return (
    <>
      <StaticLoader />
      <MosaicProvider>
        <ChameleonBrandProvider
          product="supernova"
          render={(brand) => (
            <MosaicSessionGate
              render={(user) => {
                return (
                  <>
                    <BrandedPageTitleProvider brandName={brand.brand} />
                    <FaviconProvider href={brand.favicon} />
                    <GlobalFontProvider />
                    <StyleResetProvider />
                    <ReduxProvider middlewares={middlewares} reducer={reducer}>
                      <ReduxLocaleProvider>
                        <VSTUIProvider
                          theme={{
                            base: colors[brand.Theme.base],
                            primary: colors[brand.Theme.primary],
                            secondary: colors[brand.Theme.secondary],
                          }}
                        >
                          <>
                            <PageViewFramework
                              vbid={match.params.vbid}
                              location={`${match.params.location}${location.hash}`}
                            >
                              {/* Legal obligation, so make sure we always have it
                                  unless branded off */}
                              <CookieCat brand={brand} />
                              {/* Could do per-UI, in which case configuration
                                  would need to be at least partially tied to UI support,
                                  similar to flashcard buttons. This setup is much simpler and
                                  sufficient for now */}
                              <ImageZoomFromPage />
                              <GeneralDownloadApp />
                              <DebugDialog />
                              <CustomerDebug />
                              <SkipMainButton />
                              <SkipFooterButton />
                              {getReaderUI(brand)}
                            </PageViewFramework>
                            <ToastContainer />
                          </>
                        </VSTUIProvider>
                      </ReduxLocaleProvider>
                    </ReduxProvider>
                  </>
                );
              }}
            />
          )}
        />
      </MosaicProvider>
    </>
  );
}
